import React, { useState, useEffect } from 'react';
import { X, Send, User, Clock, Tag } from 'lucide-react';
import { ticketService } from '../../../services/ticketService';
import LoadingSpinner from '../../../components/LoadingSpinner';

const TicketDetailModal = ({ ticket, onClose, onUpdate }) => {
  const [ticketDetail, setTicketDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newResponse, setNewResponse] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadTicketDetail();
  }, [ticket?.id]);

  const loadTicketDetail = async () => {
    try {
      setLoading(true);
      const data = await ticketService?.getById(ticket?.id);
      setTicketDetail(data);
    } catch (err) {
      console.error('Failed to load ticket detail:', err);
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSendResponse = async (e) => {
    e?.preventDefault();
    if (!newResponse?.trim()) return;

    try {
      setSubmitting(true);
      setError('');
      await ticketService?.addResponse(ticket?.id, newResponse);
      setNewResponse('');
      await loadTicketDetail();
    } catch (err) {
      console.error('Failed to send response:', err);
      setError(err?.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      const updated = await ticketService?.update(ticket?.id, { status: newStatus });
      onUpdate(updated);
      await loadTicketDetail();
    } catch (err) {
      console.error('Failed to update status:', err);
      setError(err?.message);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString)?.toLocaleString();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h2 className="text-2xl font-bold text-gray-900">{ticket?.subject}</h2>
              <span className="text-sm font-mono text-gray-500">{ticket?.ticketNumber}</span>
            </div>
            <div className="flex items-center gap-4 text-sm text-gray-600">
              <div className="flex items-center gap-1">
                <User className="w-4 h-4" />
                <span>{ticket?.user?.fullName}</span>
              </div>
              <div className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                <span>{formatDate(ticket?.createdAt)}</span>
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Status and Priority Controls */}
        <div className="flex items-center gap-4 p-4 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">Status:</span>
            <select
              value={ticket?.status}
              onChange={(e) => handleStatusChange(e?.target?.value)}
              className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="open">Open</option>
              <option value="in_progress">In Progress</option>
              <option value="pending_user">Pending User</option>
              <option value="resolved">Resolved</option>
              <option value="closed">Closed</option>
            </select>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">Priority:</span>
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
              ticket?.priority === 'low' ? 'text-blue-600 bg-blue-50' :
              ticket?.priority === 'medium' ? 'text-yellow-600 bg-yellow-50' :
              ticket?.priority === 'high'? 'text-orange-600 bg-orange-50' : 'text-red-600 bg-red-50'
            }`}>
              {ticket?.priority}
            </span>
          </div>
          {ticket?.tags && ticket?.tags?.length > 0 && (
            <div className="flex items-center gap-2">
              <Tag className="w-4 h-4 text-gray-600" />
              {ticket?.tags?.map((tag, index) => (
                <span key={index} className="px-2 py-1 bg-gray-200 text-gray-700 rounded text-xs">
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner />
            </div>
          ) : error ? (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-800">
              {error}
            </div>
          ) : (
            <div className="space-y-6">
              {/* Original Description */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center text-white font-semibold">
                    {ticket?.user?.fullName?.charAt(0) || 'U'}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold text-gray-900">{ticket?.user?.fullName}</span>
                      <span className="text-sm text-gray-500">{formatDate(ticket?.createdAt)}</span>
                    </div>
                    <p className="text-gray-700">{ticketDetail?.description}</p>
                  </div>
                </div>
              </div>

              {/* Responses */}
              {ticketDetail?.responses?.map((response) => (
                <div key={response?.id} className={`rounded-lg p-4 ${
                  response?.isInternal ? 'bg-yellow-50 border border-yellow-200' : 'bg-white border border-gray-200'
                }`}>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-gray-600 flex items-center justify-center text-white font-semibold">
                      {response?.user?.fullName?.charAt(0) || 'A'}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold text-gray-900">{response?.user?.fullName}</span>
                        <span className="text-sm text-gray-500">{formatDate(response?.createdAt)}</span>
                        {response?.isInternal && (
                          <span className="text-xs px-2 py-0.5 bg-yellow-200 text-yellow-800 rounded">
                            Internal Note
                          </span>
                        )}
                      </div>
                      <p className="text-gray-700 whitespace-pre-wrap">{response?.content}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Response Form */}
        <div className="border-t border-gray-200 p-4">
          <form onSubmit={handleSendResponse} className="flex gap-3">
            <textarea
              value={newResponse}
              onChange={(e) => setNewResponse(e?.target?.value)}
              placeholder="Type your response..."
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              rows="3"
              disabled={submitting}
            />
            <button
              type="submit"
              disabled={submitting || !newResponse?.trim()}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {submitting ? <LoadingSpinner /> : <Send className="w-4 h-4" />}
              Send
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default TicketDetailModal;